import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useGetFiftyTwoWeekAverageSummaryList } from '@nxsys/hooks/api/holidayPay';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { reportActions } from '@nxsys/store/slices/report/reducer/reportSlice';
import { GetNetPayListParams } from '@nxsys/store/slices/report/types';
import { Icons } from '@nxsys/types/icons';
import {
  formatFullName,
  formatStatus,
  getTaxWeekTaxYear,
  showCurrencyByCountry,
} from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { formatMoneyExport } from '@nxsys/utils/number-helper';
import { ExportToCsv } from 'export-to-csv';
import { debounce } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getFiftyTwoWeekAverageSummaryColumns } from './columns';

const FiftyTwoWeekReport: React.FC = () => {
  const { messages } = useTypedIntl();
  const navigate = useNavigateWithQuery();

  const dispatch = useDispatch();
  const { payrollCountry, isBureauEmployer, isBureau, user } = useSelector(
    (state: AppState) => state.appStore,
  );
  const { fiftyTwoWeekAverageSummaryList } = useSelector(
    (state: AppState) => state.reportStore,
  );
  const { list, pagination, loading, queryParams } =
    fiftyTwoWeekAverageSummaryList;
  const currentTaxYear = getTaxWeekTaxYear().taxYear;

  const [selectedRows, setSelectedRows] = useState<
    IFiftyTwoWeekAverageSummary[]
  >([]);

  const {
    loading: loadingFiftyTwoWeekAverageSummaryList,
    run: getFiftyTwoWeekAverageSummaryList,
  } = useGetFiftyTwoWeekAverageSummaryList();

  const handleGoBack = useCallback(() => navigate('/reports'), [navigate]);

  const renderTitle = useCallback(() => {
    return (
      <div className="flex items-center gap-x-4">
        <AppBackButton onClick={handleGoBack} />
        <AppPageTitle
          prefix={messages['common.52WeekAverageSummary']}
          separator=""
          title=""
        />
      </div>
    );
  }, [messages, handleGoBack]);

  const renderRightContent = () => {
    return (
      <div className="flex items-center gap-x-4">
        <AppButton
          type="primary"
          name={messages['common.export']}
          leftIcon={<Icons.IconArrowExport />}
          variant="outlined"
          onClick={handleExportSelected}
        />
        <AppButton
          type="primary"
          name={messages['common.exportAll']}
          leftIcon={<Icons.IconArrowExport color="#fff" />}
          onClick={handleExportAll}
        />
      </div>
    );
  };

  const handleFetchData = useCallback(
    (params: GetNetPayListParams) => {
      const newParams = {
        ...params,
        companyRefs: isBureauEmployer ? user?.companyUserIds : undefined,
      };

      dispatch(reportActions.getFiftyTwoWeekAverageSummaryList(newParams));
    },
    [dispatch, isBureauEmployer, user?.companyUserIds],
  );

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
      taxYear: currentTaxYear,
    });
  }, [currentTaxYear]);

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearch = useCallback(
    debounce(
      (e: React.ChangeEvent<HTMLInputElement>) =>
        handleFetchData({ ...queryParams, search: e.target.value }),
      500,
    ),
    [queryParams],
  );

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleFetchData({
        ...queryParams,
        sortField: sorter.field ?? '',
        sortType: sorter.order,
      });
    } else {
      handleFetchData({
        ...queryParams,
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  const handleExport = (data: IFiftyTwoWeekAverageSummary[]) => {
    const dataExport = data.map((item) => {
      const {
        subcontractor,
        earliestPayrollDate,
        paidHolidayHours,
        totalWeeksUsed,
        averageGross,
        averageHours,
      } = item;
      return {
        agencyRef: `${subcontractor?.departmentPrefix || subcontractor?.primaryAgency?.agencyPrefix || '___'}/${
          subcontractor?.agencyRef || subcontractor?.systemId || '_'
        }`,
        name: formatFullName(subcontractor),
        status: formatStatus(subcontractor?.status || ''),
        companyPayment: subcontractor?.companyPayment?.companyName || '',
        ...(isBureau ? {} : { agency: subcontractor?.primaryAgency?.name || '' }),
        earliestPayrollDate: earliestPayrollDate
          ? moment(earliestPayrollDate, 'YYYY-MM-DD').format('DD/MM/YYYY')
          : '_',
        accruedHolidayHours: (
          Number(averageHours || 0) *
          Number(totalWeeksUsed || 0) *
          0.1207
        ).toFixed(2),
        paidHolidayHours: paidHolidayHours || '',
        remainingHolidayHours: Math.max(
          0,
          Number(averageHours || 0) * Number(totalWeeksUsed || 0) * 0.1207 -
            Number(paidHolidayHours || 0),
        ).toFixed(2),
        fiftyTwoWeeksHoursWorked: (
          Number(averageHours || 0) * Number(totalWeeksUsed || 0)
        ).toFixed(2),
        fiftyTwoWeeksWeeksWorked: totalWeeksUsed || '',
        totalGross: (
          Number(averageGross || 0) * Number(totalWeeksUsed || 0)
        ).toFixed(2),
        averageGross: formatMoneyExport(
          Number(averageGross || 0),
          showCurrencyByCountry(payrollCountry),
        ),
      };
    });
    const headers = [
      isBureau ? 'Employer Ref' : 'Agency Ref',
      'Name',
      'Status',
      isBureau ? 'Employer' : 'Company Payment',
      ...(isBureau ? [] : ['Agency']),
      'Holiday Year Start',
      'Accrued Holiday Hours',
      'Paid Holiday Hours',
      'Remaining Holiday Hours',
      'Fifty Two Weeks Hours Worked',
      'Fifty Two Weeks Weeks Worked',
      'Fifty Two Weeks Total Gross',
      'Fifty Two Weeks Average Gross',
    ];
    const csvExporter = new ExportToCsv({
      filename: `52-Week-Summary-export-${moment(new Date())
        .tz('Europe/London')
        .format('DD/MM/YYYY')}`,
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      useTextFile: false,
      useBom: true,
      headers,
    });
    try {
      csvExporter.generateCsv(dataExport);
    } catch (e) {
      console.error('Export failed:', e);
      console.error(e);
    }
  };

  const handleExportSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleExport(selectedRows);
  };

  const handleExportAll = () => {
    if (!pagination.totalDocs) {
      showMessage({
        type: 'warning',
        content: messages['common.noDataToExport'],
      });
      return;
    }
    getFiftyTwoWeekAverageSummaryList({
      ...queryParams,
      page: 0,
      size: pagination.totalDocs,
    }).then((res) => {
      handleExport(res.data);
    });
  };

  const isFiltering = !!queryParams?.search;

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={loadingFiftyTwoWeekAverageSummaryList}
    >
      <AppCard>
        <AppsHeader>
          <div className="flex justify-between flex-wrap w-full gap-4">
            <div className="flex flex-wrap gap-4">
              <AppInputSearch onChange={handleSearch} />
            </div>
          </div>
        </AppsHeader>
        <AppsContent>
          <AppTableContainer
            rowKey="id"
            data={list ?? []}
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: 4000, y: 700 }}
            columns={getFiftyTwoWeekAverageSummaryColumns({
              messages,
              payrollCountry,
              isBureau,
            })}
            onChangePagination={onChangePagination}
            rowSelection={{
              type: 'checkbox',
              onChange: (_: React.Key[], selectedRows: any[]) => {
                setSelectedRows(selectedRows);
              },
            }}
            onChangeTable={onChangeTable}
            noDataType={isFiltering ? 'noDataFound' : 'noDataToShow'}
          />
        </AppsContent>
      </AppCard>
    </AppsContainer>
  );
};

export default FiftyTwoWeekReport;
